import { Carousel } from '../../components/Carousel';
import { ItemCard } from '../../components/ItemCard';
import {
  CenteredGrid,
  GridHeading,
  GridHeadingSecondary,
  RecommendationsTitle,
  RecommendationsWrapper,
} from './Recommendations.styles';
import type { RecommendationItem, RecommendationsProps } from './Recommendations.types';

function renderHeading(
  title: RecommendationsProps['title'],
  titleSecondary: RecommendationsProps['titleSecondary'],
  size: RecommendationsProps['titleSize']
) {
  if (title == null) return null;
  if (typeof title !== 'string') return title;
  if (titleSecondary == null) {
    return <GridHeading $size={size}>{title}</GridHeading>;
  }
  return (
    <GridHeading $size={size}>
      {title} <GridHeadingSecondary>{titleSecondary}</GridHeadingSecondary>
    </GridHeading>
  );
}

function renderItemCard(
  item: RecommendationItem,
  {
    fromPriceLabel,
    noImageLabel,
    onSelect,
    imageAspectRatio,
  }: {
    fromPriceLabel: string;
    noImageLabel: string;
    onSelect?: (id: string) => void;
    imageAspectRatio?: string;
  }
) {
  return (
    <ItemCard
      key={item.id}
      image={{ src: item.imageUrl, alt: item.name, placeholderLabel: noImageLabel }}
      imageAspectRatio={imageAspectRatio}
      imageOverlay
      title={item.name}
      meta={item.meta}
      price={
        item.price
          ? {
              amount: item.price.amount,
              currencyCode: item.price.currencyCode,
              label: fromPriceLabel,
              discount: item.discount,
            }
          : undefined
      }
      ariaLabel={item.name}
      onSelect={onSelect ? () => onSelect(item.id) : undefined}
    />
  );
}

export function Recommendations({
  items,
  onSelect,
  title,
  titleSecondary,
  titleSize = 'base',
  fromPriceLabel = 'From',
  noImageLabel = 'No image',
  visibleCount = { mobile: 1.2, desktop: 2 },
  layout = 'carousel',
  columns = 2,
  mobileColumns = 2,
  imageAspectRatio,
}: RecommendationsProps) {
  if (items.length === 0) return null;

  const cardOptions = { fromPriceLabel, noImageLabel, onSelect, imageAspectRatio };

  if (layout === 'grid') {
    return (
      <RecommendationsWrapper>
        {renderHeading(title ?? 'You might also like', titleSecondary, titleSize)}
        <CenteredGrid $columns={columns} $mobileColumns={mobileColumns}>
          {items.map((item) => renderItemCard(item, cardOptions))}
        </CenteredGrid>
      </RecommendationsWrapper>
    );
  }

  const carouselTitle =
    title === undefined ? <RecommendationsTitle>You might also like</RecommendationsTitle> : title;

  return (
    <RecommendationsWrapper>
      <Carousel title={carouselTitle} visibleCount={visibleCount} showProgress>
        {items.map((item) => renderItemCard(item, cardOptions))}
      </Carousel>
    </RecommendationsWrapper>
  );
}
